import { CloseOutlined, FullscreenExitOutlined, FullscreenOutlined, MinusOutlined } from '@ant-design/icons'
import { Button, Space } from 'antd'
import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { Rnd } from 'react-rnd'
import './index.less'
import classNames from 'classnames'

export const CLOSE_TYPES = {
  close: 'close',
  submit: 'submit',
  minimize: 'minimize'
}

interface DialogProps {
  className?: string
  width?: number
  height?: number
  minWidth?: number
  minHeight?: number
  showResizeAction?: boolean
  x?: number
  y?: number
  visible?: boolean
  title: string | null
  children
  footer?
  zIndex?: number
  onOpen?: () => void
  onClose?: (type: string) => void
  onSubmit?: () => boolean
  onMaximize?: () => void
  onMinimize?: () => void
  onNormalize?: () => void
  onMouseDown?: (e) => void
}

const Dialog = (props: DialogProps) => {
  const innerWidth = window.innerWidth
  const {
    width = 500,
    height = 300,
    minWidth = 500,
    minHeight = 300,
    x = (innerWidth - width) / 2 + 100 * Math.random(),
    y = 100 + 100 * Math.random(),
    visible = false,
    zIndex = 1000,
    title,
    children,
    footer,
    onOpen,
    onClose,
    onMaximize,
    onMinimize,
    onNormalize,
    onSubmit,
    onMouseDown,
    showResizeAction,
    className
  } = props
  const [size, setSize] = useState<any>({ width, height })
  const [position, setPosition] = useState<any>({ x, y })
  const [isFull, setIsFull] = useState(false)
  const [cache, setCache] = useState<any>()

  useEffect(() => {
    if (visible) {
      onOpen?.()
    }
  }, [visible])

  const handleMaximize = () => {
    setIsFull(true)
    setCache({ ...size, ...position })
    setPosition({ x: 0, y: 0 })
    setSize({ width: '100%', height: '100%' })
    onMaximize?.()
  }

  const handleNormalize = () => {
    setIsFull(false)
    setPosition({ x: cache.x, y: cache.y })
    setSize({ width: cache.width, height: cache.height })
    onNormalize?.()
  }

  const handleMinimize = () => {
    onMinimize?.()
    onClose?.(CLOSE_TYPES.minimize)
  }

  const handleClose = () => {
    onClose?.(CLOSE_TYPES.close)
  }

  const handleSubmit = async () => {
    const isTrue = await onSubmit?.()
    if (isTrue) {
      onClose?.(CLOSE_TYPES.submit)
    }
  }

  const handleSetPosition = (x, y) => {
    setPosition({ x, y: Math.max(y, 0) })
  }

  if (!visible) {
    return null
  }

  return (
    <div className={classNames('orbit-dialog-wrapper', className)} style={{ zIndex: zIndex }}>
      <Rnd
        size={size}
        cancel='.cancel'
        position={position}
        minWidth={minWidth}
        minHeight={minHeight}
        className='orbit-dialog-rnd'
        enableResizing={!isFull}
        disableDragging={isFull}
        onDragStop={(e, d) => {
          handleSetPosition(d.x, d.y)
        }}
        style={{
          left: position.x,
          top: position.y
        }}
        onDrag={(e,d) => {
          handleSetPosition(d.x, d.y)
        }}
        onResizeStop={(e, direction, ref, delta, p) => {
          handleSetPosition(p.x, p.y)
          setSize({ width: ref.offsetWidth, height: ref.offsetHeight })
        }}
      >
        <div
          className='ant-modal-content'
          onMouseDown={(e: any) => {
            onMouseDown?.(e)
          }}
        >
          <div className='ant-modal-header'>
            <div className='ant-modal-title'>{title}</div>

            <Space>
              {showResizeAction && <Button icon={<MinusOutlined />} onClick={handleMinimize} />}
              {showResizeAction && isFull && <Button icon={<FullscreenExitOutlined />} onClick={handleNormalize} />}
              {showResizeAction && !isFull && <Button icon={<FullscreenOutlined />} onClick={handleMaximize} />}

              <Button icon={<CloseOutlined />} onClick={handleClose} />
            </Space>
          </div>
          <div className='no-drag-container cancel'>
            <div className='ant-modal-body'>{children}</div>
            {footer !== undefined ? (
              footer
            ) : (
              <div className='ant-modal-footer'>
                <Button onClick={handleClose}>Cancel</Button>
                <Button type='primary' onClick={handleSubmit}>
                  OK
                </Button>
              </div>
            )}
          </div>
        </div>
      </Rnd>
    </div>
  )
}

Dialog.show = (props: DialogProps) => {
  const div = document.createElement('div')
  document.body.appendChild(div)
  const cancel = () => div.remove()

  ReactDOM.render(
    <Dialog
      {...props}
      visible={true}
      onClose={(type: string) => {
        cancel()
        props.onClose?.(type)
      }}
    />,
    div
  )
}

export default Dialog
